import { useEffect, useState } from "react";
import { api } from "./api-client.js";
import type { DashboardTodayResponse } from "@wmart/shared-types";

/**
 * Module-level cache + subscriber list so every component that calls
 * `useApplicationsBadge()` shares a single fetched value.  The cache is
 * invalidated imperatively via `refresh()` — callers (approve / reject
 * modals) call it after a successful mutation so the badge ticks down
 * without a page reload.
 *
 * `cached === null`  ⟹  not yet fetched (first subscriber triggers the
 * initial fetch).  `cached >= 0`  ⟹  value known and shared.
 */
let cached: number | null = null;
let refetchers: ((n: number) => void)[] = [];

async function refresh(): Promise<void> {
  try {
    const dash = await api.get<DashboardTodayResponse>(
      "/v1/admin/dashboard/today",
    );
    const count = dash?.pending_applications_count;
    if (typeof count !== "number") return;
    cached = count;
    refetchers.forEach((f) => f(cached!));
  } catch {
    // Silently swallow — a badge failing to load should never crash the shell.
    // The count stays at its last known value (or 0 if never fetched).
  }
}

/**
 * Hook: returns the current `pending_applications_count` from the admin
 * dashboard endpoint, plus a `refresh` function to invalidate the cache.
 *
 * The first component to mount triggers a single fetch; subsequent mounts
 * reuse the cached value immediately.  All mounted subscribers are updated
 * together when `refresh()` resolves.
 *
 * Usage:
 * ```tsx
 * const { count, refresh } = useApplicationsBadge();
 * // render <Badge>{count}</Badge> in the nav; call refresh() after approve/reject
 * ```
 */
export function useApplicationsBadge(): {
  count: number;
  refresh: () => Promise<void>;
} {
  const [count, setCount] = useState(cached ?? 0);

  useEffect(() => {
    refetchers.push(setCount);
    // Sync local state if the cache was already populated before this mount.
    if (cached !== null) setCount(cached);
    else void refresh();
    return () => {
      refetchers = refetchers.filter((f) => f !== setCount);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { count, refresh };
}

/**
 * Imperatively clear the module-level cache without triggering a refetch.
 * The next `useApplicationsBadge` subscriber that mounts (or a manual
 * `refresh()` call) will re-populate the cache.  Use this from contexts
 * where you want to guarantee a fresh read on next mount rather than
 * pushing an update to current subscribers.
 */
export function invalidateApplicationsBadgeCache(): void {
  cached = null;
}
